import FFLDocumentReview from "./FFLDocumentReview";
import FFLExpirationVerification from "./FFLExpirationVerification";
import WebsitePauseConfirmation from './WebsitePauseConfirmation';
import WebsiteHostingCancellationRequest from "./WebsiteHostingCancellationRequest";
import { useWebsiteTrialAcceptance, websiteStatusErrorMessage } from "./WebsiteTrialAcceptance";
// components/FFLClaims.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Config from "../utils/Config";
import {
    formatFflExpirationDate,
    formatDateTime,
    viewWebsite,
    getWebsiteAddress,
    getFflExpirationDaysLeft,
    getFflExpirationClass,
    getFileExtension,
    getFileIcon,
    displayMessage,
} from "../utils/functions";
import InlineVerificationCode from './VerifyCodePanel';
import EmailVerificationDialog from './EmailVerificationDialog';
import useTitle from '../hooks/useTitle';
import { toast } from "react-toastify";
import SearchBox from "./SearchBox";
import Pager from "./Pager";
import DealerOverview from "./DealerOverview";
import DealerWebsiteSubscription from "./DealerWebsiteSubscription";
import "./Login.css";
import "./FFLClaims.css";

const PAGE_SIZES = [10, 25, 50];

// Display only explicit server status/cancellation evidence; never infer entitlement from a 404.
function adminHostingStatus(claim, now = Date.now()) {
    const summary = claim.websiteSubscription, entitlement = claim.websiteEntitlement;
    const record = claim.subscription?.cancellation;
    const effective = record?.effectiveAt || claim.subscription?.cancelledAt;
    const validEffective = typeof effective === 'string' && effective.trim() && Number.isFinite(+new Date(effective));
    const legacyCancelled = claim.subscription?.cancelledAt;
    const cancelled = summary?.status === 'canceled' || entitlement?.state === 'canceled' || Boolean(legacyCancelled && Number.isFinite(+new Date(legacyCancelled))) || Boolean(validEffective && +new Date(effective) <= now);
    const state = summary?.status || entitlement?.state;
    const labels = { trial: 'Free Trial', trial_ready: 'Trial not started', active: 'Paid / Active', grace: 'Grace Period', expired: 'Expired', legacy: 'Legacy', canceled: 'Cancelled' };
    return { cancelled, label: cancelled ? 'Cancelled' : record && validEffective ? 'Cancellation Scheduled' : labels[state] || 'Status not supplied', effectiveAt: validEffective ? effective : null,
        publicAvailable: cancelled ? false : typeof summary?.publiclyAvailable === 'boolean' ? summary.publiclyAvailable : typeof entitlement?.allowed === 'boolean' ? claim.status === 'Approved' && claim.siteStatus === 'Published' && entitlement.allowed : null };
}
function validLicenseExpiration(value) {
    return typeof value === 'string' && value.trim() !== '' && Number.isFinite(+new Date(value));
}

// Reset claim and selection state before rendering a different authenticated account.
function FFLClaims(props) {
    const { user } = useContext(AuthContext);
    const userId = user?.id || user?._id;
    return userId ? <FFLClaimsContent key={String(userId)} {...props} /> : null;
}

function FFLClaimsContent({
    status,                    // e.g. "Pending" or "Pending,Cancelled" or "Approved,Live"
    overview = false,
    onOpenSupportInbox,
    focusSupportForm = false,
    onSupportFocused,
    mode = "user",            // "user" | "admin"
    tips = [],                // optional tips array for the user variant
}) {
    const trial = useWebsiteTrialAcceptance();
    const isAdmin = mode === "admin";
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();

    const openFflDocument = async (event, claim) => {
        event.preventDefault();
        try {
            const response = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_DOWNLOAD_FFL}/${claim._id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            });
            if (!response.ok) throw new Error("Unable to retrieve FFL document.");
            const documentUrl = URL.createObjectURL(await response.blob());
            // Preserve the existing endpoint's attachment-download behavior.
            const link = document.createElement("a");
            link.href = documentUrl;
            link.download = claim.filePath?.split("/").pop() || "ffl-document";
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(documentUrl), 60000);
        } catch (error) {
            toast.error("Unable to retrieve FFL document. Please sign in again or retry.");
        }
    };

    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [claimsReload, setClaimsReload] = useState(0);

    const [password, setPassword] = useState("");
    const [cancelReason, setCancelReason] = useState("");
    const [passwordError, setPasswordError] = useState(null);

    const [selectedClaim, setSelectedClaim] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [hostingCancellationClaim, setHostingCancellationClaim] = useState(null);
    const [showApproveModal, setShowApproveModal] = useState(false);

    const [siteOnById, setSiteOnById] = useState({});
    const [pauseClaim, setPauseClaim] = useState(null);
    const [sitePendingById, setSitePendingById] = useState({});
    const siteRequests = useRef(new Set());

    const passwordRef = useRef(null);
    const messageRef = useRef(null);

    const [mfaPending, setMfaPending] = useState(false);
    const [mfaModalOpen, setMfaModalOpen] = useState(false);
    const [mfaChallengeId, setMfaChallengeId] = useState(null);
    const [mfaExpiresAt, setMfaExpiresAt] = useState(null);
    const [mfaAction, setMfaAction] = useState(null); // 'enable' | 'disable'
    const [pendingMessage, setPendingMessage] = useState("");
    const [pendingStyle, setPendingStyle] = useState("warning");
    const [adminLevel, setAdminLevel] = useState("warning");
    const [messageClass, setMessageClass] = useState("hidden");
    const [challengeAction, setChallengeAction] = useState(null);

    const [formData, setFormData] = useState({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        useMFA: user?.useMFA || false,
        timeZone: user?.timeZone || "",
    });

    // paging
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

    // scroll to top on page change
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }, [page]);

    // Titles / icons
    const title = useMemo(() => {
        if (isAdmin) return status.includes("Pending") ? "Pending / Cancelled" : "Verified Websites";
        return status === "Pending" ? "Pending Claims" : "My Dealerships";
    }, [status, isAdmin]);

    const titleIcon = useMemo(() => (status.includes("Pending") ? "hourglass-half" : "fa-globe"), [status]);

    // Reason options (you can tweak per mode)
    const reasonOptions = useMemo(() => {
        if (isAdmin) {
            return [
                ["", "Select reason"],
                ["missing_docs", "Missing Docs"],
                ["duplicate_owner", "Duplicate Owner"],
                ["fraud", "Fraud"],
                ["user_cancelled", "User Cancelled"],
                ["other", "Other"],
            ];
        }
        return [
            ["", "Select reason"],
            ["no_longer_in_business", "No longer in business"],
            ["switched_provider", "Switched to another provider"],
            ["ownership_change", "Business sold / ownership change"],
            ["incorrect_claim", "Incorrect claim (wrong FFL/location)"],
            ["website_not_needed", "Website not needed"],
            ["pricing_issues", "Fees / pricing issues"],
            ["insufficient_time", "Insufficient time to manage"],
            ["privacy_concerns", "Privacy concerns"],
            ["technical_issues", "Technical issues during setup"],
            ["duplicate_claim", "Duplicate claim by another user"],
            ["license_expired", "License expired / suspended"],
            ["fraudulent_claim", "Fraudulent or mistaken claim"],
            ["other", "Other"],
        ];
    }, [isAdmin]);

    // Small helpers
    const formatCancelReason = (reason) =>
        (reason || "")
            .split("_")
            .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
            .join(" ");

    const randomTip = useMemo(
        () => (Array.isArray(tips) && tips.length ? tips[Math.floor(Math.random() * tips.length)] : null),
        [tips]
    );

    // helper to reset MFA state
    const clearMfaState = () => {
        setMfaPending(false);
        setMfaModalOpen(false);
        setMfaChallengeId(null);
        setMfaExpiresAt(null);
        setMfaAction(null);
        setPendingMessage("");
        setMessageClass("hidden");
    };

    // 🔎 search
    const [query, setQuery] = useState("");

    const searchableText = (c) => {
        // Build a lowercase “haystack” for each card
        return [
            c.fflNumber,
            c.fflName,
            c.businessName,
            c.subdomain,
            c.status,
            c.email,                // claimant email
            isAdmin ? c.cancelledByEmail : null,
            isAdmin ? c.approvedByEmail : null,
            c.cancelReason ? formatCancelReason(c.cancelReason) : null,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
    };
    
    useTitle(overview ? "Dealer Dashboard" : isAdmin ? "Websites" : title);

    // NEW: filtered claims based on query
    const filteredClaims = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return claims;

        return claims.filter((c) => {
            const parts = [
                c.subdomain,
                c.fflNumber,
                c.fflName,
                c.businessName,
                c.email,
                c.cityStateZip,
                c.cancelReason,   // so you can search by cancel reason text too
            ];
            return parts.some(v => (v || "").toLowerCase().includes(needle));
        });
    }, [claims, query]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    // keep page in bounds when list/query/pageSize changes
    useEffect(() => { setPage(1); }, [query, pageSize, status, isAdmin]);
    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredClaims.length / pageSize));
        if (page > maxPage) setPage(maxPage);
    }, [filteredClaims.length, pageSize]);

    // Load list
    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;
        setClaims([]);
        setSiteOnById({});
        setError(null);
        setLoading(true);

        (async () => {
            try {
                const endpoint = isAdmin
                    ? Config.ENDPOINTS.DEALERS_ALL_FFL_CLAIMS_BY_STATUS
                    : Config.ENDPOINTS.DEALERS_MY_FFLS_BY_STATUS;

                const res = await fetch(
                    `${Config.API.API_BASE_URL}${endpoint}?status=${encodeURIComponent(status)}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }, signal: controller.signal }
                );

                const data = await res.json().catch(() => null);
                if (cancelled) return;
                if (!res.ok) throw new Error(data?.message || "Failed to fetch claims.");
                setClaims(Array.isArray(data) ? data : []);
                if (Array.isArray(data)) {
                    const seeded = Object.fromEntries(
                        data.map(c => [c._id, c.siteStatus === "Published"])
                    );
                    setSiteOnById(seeded);
                }
            } catch (e) {
                if (!cancelled) setError(e.message || "Error fetching FFL claims.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [user, status, isAdmin, claimsReload]);

    // Edit (user only)
    const handleEditClaim = (claim, setupSection = null) => {
        if (!isAdmin) {
            localStorage.removeItem("fflClaimedOnce");
            localStorage.setItem("fflEditData", true);
            localStorage.setItem("claimId", claim._id);
            localStorage.setItem("dealerId", claim.dealerId);

            navigate("/dashboard", {
                state: {
                    activeTab: "claim-ffl",
                    ...(claim.status === "Approved" && ["fflUpload", "dealerSite", "dealerType", "businessHours", "siteStatus", "dealershipInfo"].includes(setupSection) ? { setupSection } : {}),
                    fflNumber: claim.fflNumber,
                    fflName: claim.fflName,
                    claimId: claim._id,
                    dealerId: claim.dealerId,
                },
            });
        }
    };

    // Claim cancellation remains distinct from a hosting-only support request.
    const openDeleteModal = (claim) => {
        if (!isAdmin && ["Approved", "Live"].includes(claim.status)) {
            setHostingCancellationClaim(claim);
            return;
        }
        setPassword("");
        setCancelReason("");
        setPasswordError(null);
        setSelectedClaim(claim);
        setShowDeleteModal(true);
    };
    const closeDeleteModal = (resetReason = true, clearSelection = true) => {
        setShowDeleteModal(false);
        if (clearSelection) setSelectedClaim(null);
        setPassword("");
        if (resetReason) setCancelReason("");
        setPasswordError(null);
    };
    const handleDeleteClaim = async () => {
        if (!selectedClaim) return;
        if (!isAdmin && ["Approved", "Live"].includes(selectedClaim.status)) {
            const claim = selectedClaim;
            closeDeleteModal();
            setHostingCancellationClaim(claim);
            return;
        }

        try {
            const mfaAction = "cancel-claim";
            setMfaPending(true);
            setPasswordError(null);

            const r = await fetch(
                `${Config.API.API_BASE_URL}${Config.ENDPOINTS.ADMIN_CLAIM_START}`, // <— add in Config
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    body: JSON.stringify({ claimId: selectedClaim._id, password, mfaAction }),
                }
            );

            const data = await r.json().catch(() => ({}));
            if (!r.ok || !data?.challengeId) {
                // bad password or other issue
                setPassword("");
                setPasswordError(data?.message || "Failed to start verification. Check password.");
                passwordRef.current?.focus();
                setMfaPending(false);
                setChallengeAction(mfaAction);
                return;
            }

            // 2) Close password modal; open MFA modal
            closeDeleteModal(false, false);
            setMfaAction(mfaAction);
            setMfaChallengeId(data.challengeId);
            setMfaExpiresAt(data.expiresAt || null);

            setPendingMessage(data.message || "✅ Code resent. Check your email.");
            setMessageClass("success");
            setPendingStyle("success");

            setMfaModalOpen(true);
        } catch (e) {
            console.error("Error approving claim:", e);
            setPassword("");
            setPasswordError("Server error starting approval.");
        } finally {
            setMfaPending(false);
        }
    };

    // Approve (admin only)
    const openApproveModal = (claim) => {
        setSelectedClaim(claim);
        setPassword("");
        setPasswordError(null);
        setShowApproveModal(true);
    };

    const closeApproveModal = (clearSelection = true) => {
        setShowApproveModal(false);
        if (clearSelection) setSelectedClaim(null);
        setPassword("");
        setPasswordError(null);
    };

    const handleApproveClaim = async () => {
        if (!selectedClaim) return;

        try {
            const mfaAction = "approve-claim";
            setMfaPending(true);
            setPasswordError(null);

            // 1) Server validates password AND starts an MFA challenge
            // Expecting response: { challengeId, expiresAt }
            const r = await fetch(
                `${Config.API.API_BASE_URL}${Config.ENDPOINTS.ADMIN_CLAIM_START}`, // <— add in Config
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    body: JSON.stringify({ claimId: selectedClaim._id, password, mfaAction }),
                }
            );

            const data = await r.json().catch(() => ({}));
            if (!r.ok || !data?.challengeId) {
                // bad password or other issue
                setPassword("");
                setPasswordError(data?.message || "Failed to start verification. Check password.");
                passwordRef.current?.focus();
                setMfaPending(false);
                setChallengeAction(mfaAction);
                displayMessage(messageRef, data?.message || "Invalid code", "warning", setMessageClass);
                return;
            }

            // 2) Close password modal; open MFA modal
            closeApproveModal(false);
            setMfaAction(mfaAction);
            setMfaChallengeId(data.challengeId);
            setMfaExpiresAt(data.expiresAt || null);

            setPendingMessage(data.message || "✅ Code resent. Check your email.");
            setMessageClass("success");
            setPendingStyle("success");

            setMfaModalOpen(true);
        } catch (e) {
            console.error("Error approving claim:", e);
            setPassword("");
            setPasswordError("Server error starting approval.");
        } finally {
            setMfaPending(false);
        }
    };

    const requestSiteToggle = (claim) => {
        if (siteRequests.current.has(claim._id)) return;
        if (siteOnById[claim._id] ?? (claim.siteStatus === "Published")) setPauseClaim(claim);
        else toggleSite(claim);
    };

    const toggleSite = async (claim) => {
        if (siteRequests.current.has(claim._id)) return;
        const wasOn = siteOnById[claim._id] ?? (claim.siteStatus === "Published");
        const nextOn = !wasOn;
        const setupMessage = "Complete your website address, theme, specialties, and business hours in Manage Dealership → Website Information before publishing.";
        if (nextOn && (!claim.subdomain || !claim.theme ||
            !(claim.specialties?.length || claim.customSpecialties?.length) ||
            (claim.byAppointmentOnly !== true && !Object.values(claim.businessHours || {}).some(day => day && !day.closed && day.open && day.close)))) {
            toast.error(setupMessage);
            return;
        }

        siteRequests.current.add(claim._id);
        setSitePendingById(prev => ({...prev, [claim._id]: nextOn ? "Publishing…" : "Pausing…"}));
        try {
        const permission = nextOn ? await trial.request(claim._id) : {allowed:true};
        if (!permission.allowed) return;
        // optimistic UI: flip the toggle and the label
        setSiteOnById(prev => ({ ...prev, [claim._id]: nextOn }));
        setClaims(prev =>
            prev.map(c => c._id === claim._id
                ? { ...c, siteStatus: nextOn ? "Published" : "Paused" }
                : c
            )
        );

        let failureMessage = "Couldn't update website status. Please try again.";
        try {
            const res = await fetch(
                `${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_TOGGLE_SITE_STATUS}/${claim._id}`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                    body: JSON.stringify({ isActive: nextOn, trialAcceptance: permission.acceptance }),
                }
            );

            const data = await res.json().catch(() => null);
            if (!res.ok) {
                // Only known endpoint validation messages may reach the dealer.
                if (websiteStatusErrorMessage(data, res.status)) failureMessage = websiteStatusErrorMessage(data, res.status);
                if (res.status === 409 && data?.message === "Complete your website address, theme, specialties, and business hours before publishing.") {
                    failureMessage = setupMessage;
                } else if (res.status === 409 && ["An approved claim is required to publish.", "Claim changed or access denied. Please refresh."].includes(data?.message)) {
                    failureMessage = data.message;
                } else if (res.status === 401) {
                    failureMessage = "Please sign in again before updating your website status.";
                } else if (res.status === 403) {
                    failureMessage = "You do not have permission to update this website.";
                } else if (res.status === 404 && data?.message === "Dealer not found") {
                    failureMessage = "This dealership is unavailable. Refresh your dealerships and try again.";
                }
                throw new Error("Website status update rejected.");
            }
            if (data?.siteStatus !== (nextOn ? "Published" : "Paused")) {
                failureMessage = "The website status change could not be confirmed. Refresh your dealerships to check its current status.";
                throw new Error("Unexpected website status response.");
            }
            setClaims(prev => prev.map(c => c._id === claim._id ? {...c, ...(data.websiteSubscription ? {websiteSubscription:data.websiteSubscription} : {})} : c));
            toast.success("✅ Updated site status to " + (nextOn ? "Published" : "Paused") + ".");
        } catch {
            // rollback on error
            setSiteOnById(prev => ({ ...prev, [claim._id]: wasOn }));
            setClaims(prev =>
                prev.map(c => c._id === claim._id
                    ? { ...c, siteStatus: wasOn ? "Published" : "Paused" }
                    : c
                )
            );
            toast.error(failureMessage);
        }
        } finally {
            siteRequests.current.delete(claim._id);
            setSitePendingById(prev => ({...prev, [claim._id]: null}));
        }
    };

    const isDeleteDisabled = password.length < 8 || (isAdmin && cancelReason === ""); // admin allows empty? keep if you want required
    const isApproveDisabled = password.length < 8;

    const total = claims.length;
    const filteredCount = filteredClaims.length;
    const pageCount = Math.max(1, Math.ceil(filteredCount / pageSize));
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * pageSize;
    const rows = filteredClaims.slice(start, start + pageSize);
    const shown = rows.length;
    const hasQuery = query.trim().length > 0;

    return (
        <div className={overview ? "dealer-overview-host" : "login-container login-container-dashboard"}>
            {trial.dialog}
            {pauseClaim && <WebsitePauseConfirmation onCancel={() => setPauseClaim(null)} onConfirm={() => { const claim = pauseClaim; setPauseClaim(null); toggleSite(claim); }} />}
            {hostingCancellationClaim && <WebsiteHostingCancellationRequest key={hostingCancellationClaim._id} claim={hostingCancellationClaim} onClose={() => setHostingCancellationClaim(null)} onOpenSupportInbox={onOpenSupportInbox} />}
            {overview ? <DealerOverview onRetry={() => setClaimsReload(value => value + 1)} claims={claims} loading={loading} error={error} user={user} onManage={handleEditClaim} onToggle={requestSiteToggle} sitePendingById={sitePendingById} onCancel={openDeleteModal} onFindDealer={() => navigate("/")} onOpenSupportInbox={onOpenSupportInbox} focusSupportForm={focusSupportForm} onSupportFocused={onSupportFocused} /> : <div className="login-box">
                <header className="claims-h claims-header fflClaims-search-box">
                    <h2 className={!isAdmin && status === "Approved,Live" ? "dashboard-title" : undefined}>
                        <span className="titleIcon"><FontAwesomeIcon icon={titleIcon} /></span>&nbsp;&nbsp;{title}
                    </h2>
                    <SearchBox
                        filteredClaims={filteredClaims}
                        onChange={setQuery}
                        placeholder={isAdmin ? "Search sites..." : status === "Pending" ? "Search pending claims..." : "Search my dealerships..."}
                        ariaLabel={isAdmin ? "Search websites" : status === "Pending" ? "Search pending claims" : "Search my dealerships"}
                        shown={filteredCount}
                        total={total}
                        hasQuery={hasQuery}
                        query={query}
                        setQuery={setQuery}
                    />
                </header>

                {/* table/card controls: rows per page */}
                <div className="admin-table-controls users-tools mobile-search-box">
                    <div className="admin-table-right">
                        <label className="rows-label">Rows:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            className="rows-select"
                        >
                            {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                </div>

                {loading && <p>Loading...</p>}
                {error && <p className="error">{error}</p>}

                {!loading && !error && claims.length === 0 && (
                    <p className="dimmed">
                        {isAdmin ? `There are no ${status} FFL claims.` : (
                            <>
                                {status === "Pending" ? "You don’t have any pending claims at this time." : "You don’t have any approved dealerships at this time."}&nbsp;
                                <span onClick={() => navigate("/")} className="cta-claim-link">Claim yours now to get started</span>.
                            </>
                        )}
                    </p>
                )}

                {!loading && !error && claims.length > 0 && (
                    filteredClaims.length === 0 ? (
                        <p className="dimmed">No matches for “{query}”.</p>
                    ) : (
                        <ul className="pending-claims-list" id="pending-claims-list">
                            {rows.map((claim) => {
                                const hosting = isAdmin ? adminHostingStatus(claim) : null;
                                const expirationValid = validLicenseExpiration(claim.expirationDate);
                                return (
                                <li
                                    key={claim._id}
                                    className={`claim-card ${claim.status ? `${claim.status.toLowerCase()}-card-border` : ""}${isAdmin && claim.status === "Approved" ? " verified-admin-card" : ""}`}
                                >
                                    {isAdmin && (
                                        <div className="claim-info">
                                            <span className="claim-info-title"><strong>Claimant Email:</strong></span>{claim.email.toLowerCase()}
                                        </div>
                                    )}
                                    {claim.status === "Approved" && (
                                    <div className="claim-info">
                                        <span className="claim-info-title"><strong>Domain:</strong></span>
                                        {isAdmin && hosting.cancelled ? <span>{getWebsiteAddress(claim?.subdomain)} <span className="dimmed">(reserved; hosting cancelled)</span></span> : <a
                                            href={getWebsiteAddress(claim?.subdomain)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                viewWebsite(claim?.subdomain, claim?.viewToken, claim?.status, claim?._id, claim?.siteStatus, claim?.websiteSubscription?.publiclyAvailable);
                                            }}
                                        >
                                            {getWebsiteAddress(claim?.subdomain)}
                                        </a>}
                                    </div>
                                    )}

                                    <div className="claim-info">
                                        <span className="claim-info-title"><strong>FFL Number:</strong></span>
                                        <a target="_blank" rel="noopener noreferrer" href={`${Config.API.SITE_BASE_URL}/dealer/${claim.dealerId}`}>{claim.fflNumber}</a>
                                    </div>

                                    <div className="claim-info">
                                        <strong className="claim-info-title">FFL Name:</strong>
                                        {claim.fflName || <span className="dimmed">Not provided</span>}
                                    </div>

                                    <div className="claim-info">
                                        <strong className="claim-info-title">Business Name:</strong>
                                        {claim.businessName || <span className="dimmed">Not provided</span>}
                                    </div>

                                    {/* Overall status */}
                                    <div className="claim-info">
                                        <span className="claim-info-title"><strong>{isAdmin && claim.status === "Approved" ? "Claim:" : "Claim Status:"}</strong></span>
                                        <span className={`status-dot ${claim.status.toLowerCase()}`}></span>
                                        <span className={`status-text ${claim.status.toLowerCase()}`}>{claim.status}</span>
                                    </div>

                                    {/* Canceled details */}
                                    {claim.status === "Cancelled" && (
                                        <>
                                            <div className="claim-info">
                                                <span className="claim-info-title"><strong>Canceled on:</strong></span>
                                                <span className="claim-date"><span className="cancelled-text">{formatDateTime(claim.cancelledAt)}</span></span>
                                            </div>
                                            <div className="claim-info">
                                                <span className="claim-info-title"><strong>Canceled by:</strong></span>
                                                <span className="claim-date"><span className="cancelled-text">{claim.cancelledByEmail}</span></span>
                                            </div>
                                            <div className="claim-info">
                                                <span className="claim-info-title"><strong>Cancel reason:</strong></span>
                                                <span className="claim-date"><span className="cancelled-text">{formatCancelReason(claim.cancelReason)}</span></span>
                                            </div>
                                        </>
                                    )}

                                    {/* Site status (when not Pending or Cancelled) */}
                                    {claim.status === "Approved" && (
                                        <div className="claim-info">
                                            <span className="claim-info-title"><strong>{isAdmin ? "Website configuration:" : "Website Status:"}</strong></span>
                                            <div className="claim-info-value status-value">
                                                <span className={`status-dot ${claim.siteStatus.toLowerCase()}`} />
                                                <span className={`status-text site-status-text ${claim.siteStatus.toLowerCase()}`}>
                                                    {claim.siteStatus}
                                                </span>
                                                {(() => {
                                                    const siteOn = siteOnById[claim._id] ?? (claim.siteStatus === "Published");
                                                    return (
                                                        <span className="website-status-action">
                                                        <button
                                                            type="button"
                                                            className={`toggle ${siteOn ? "on" : "off"}`}
                                                            onClick={() => requestSiteToggle(claim)}
                                                            disabled={Boolean(sitePendingById[claim._id]) || Boolean(isAdmin && hosting.cancelled)}
                                                            aria-busy={Boolean(sitePendingById[claim._id])}
                                                            aria-pressed={siteOn}
                                                            aria-label={siteOn ? "Pause website" : "Publish website"}
                                                            title={isAdmin && hosting.cancelled ? "Hosting is cancelled. Publishing configuration is retained." : siteOn ? "Pause Website" : "Publish Website"}
                                                        >
                                                            <span className="knob" />
                                                        </button>
                                                        <span aria-live="polite">{isAdmin && hosting.cancelled ? "Hosting cancelled; configuration retained" : sitePendingById[claim._id] || (siteOn ? "Pause Website" : "Publish Website")}</span>
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {isAdmin && claim.status === "Approved" && <section className="verified-hosting-summary" aria-label="Hosting and public availability">
                                        <div className="claim-info"><strong className="claim-info-title">Hosting:</strong><span>{hosting.label}</span></div>
                                        {hosting.effectiveAt && <div className="claim-info"><strong className="claim-info-title">Cancellation effective:</strong><span>{formatDateTime(hosting.effectiveAt)}</span></div>}
                                        <div className="claim-info"><strong className="claim-info-title">Public website:</strong><span>{hosting.publicAvailable === false ? 'Unavailable' : hosting.publicAvailable === true ? 'Available' : 'Availability not supplied'}</span></div>
                                        {hosting.cancelled && <p>Hosting cancellation does not cancel the approved dealership claim or remove its website configuration, reserved address or saved content.</p>}
                                    </section>}
                                    <div className="claim-info">
                                        <span className="claim-info-title"><strong>License Expires:</strong></span>
                                        <span className={`claim-date ${expirationValid ? getFflExpirationClass(claim.expirationDate) : 'expiration-unknown'}`}>
                                            {expirationValid ? <>{formatFflExpirationDate(claim.expirationDate)} ({getFflExpirationDaysLeft(claim.expirationDate)} days left)</> : 'Unknown'}
                                        </span>
                                    </div>

                                    <div className="claim-info">
                                        <span className="claim-info-title"><strong>Submitted on:</strong></span>
                                        <span className="claim-date">{formatDateTime(claim.createdAt)}</span>
                                    </div>

                                    {claim.status !== "Pending" && claim.approvedAt && (
                                        <div className="claim-info">
                                            <span className="claim-info-title"><strong>Approved on:</strong></span>
                                            <span className="claim-date">{formatDateTime(claim.approvedAt)}</span>
                                        </div>
                                    )}

                                    <div className="claim-info">
                                        <span className="claim-info-title"><strong>Last Updated:</strong></span>
                                        <span className="claim-date">{formatDateTime(claim.lastUpdated)}</span>
                                    </div>

                                    {claim.status !== "Pending" && (() => {
                                        const ym = new Date().toISOString().slice(0, 7);
                                        const viewsThisMonth = claim.viewsByMonth?.[ym] || 0;
                                        return viewsThisMonth > 0 ? (
                                            <div className="claim-info">
                                                <span className="claim-info-title"><strong>Views this Month:</strong></span>
                                                {viewsThisMonth}
                                            </div>
                                        ) : null;
                                    })()}

                                    {claim.status !== "Pending" && claim.viewsTotal > 0 && (
                                        <div className="claim-info">
                                            <span className="claim-info-title"><strong>Total Views:</strong></span>
                                            <span className="claim-date">{claim.viewsTotal}</span>
                                        </div>
                                    )}

                                    <div className="claim-info claim-info-row">
                                        <span className="claim-info-title claim-info-title-ffl"><strong>FFL File:</strong></span>
                                        <span className="claim-info-value">
                                            {getFileIcon(getFileExtension(claim.filePath))} {getFileExtension(claim.filePath)}{" "}•{" "}
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_DOWNLOAD_FFL}/${claim._id}`}
                                                onClick={(event) => openFflDocument(event, claim)}
                                                onAuxClick={(event) => { if (event.button === 1) openFflDocument(event, claim); }}
                                            >
                                                <span className="ffl-file-desktop">
                                                    Uploaded:&nbsp;{formatDateTime(claim.renewalUploadedAt)}
                                                </span>
                                                <span className="ffl-file-mobile">
                                                    View License
                                                </span>
                                            </a>
                                        </span>
                                    </div>

                                    {!isAdmin && !overview && claim.subdomain && (
                                        <DealerWebsiteSubscription summary={claim.websiteSubscription} dealerName={claim.businessName || claim.fflName} onContactSupport={onOpenSupportInbox} />
                                    )}
                                    <br />
                                    {isAdmin && <FFLExpirationVerification key={`expiration-${claim._id}`} claim={claim} onSaved={expirationDate => {
                                        setClaims(prev => prev.map(c => c._id === claim._id ? { ...c, expirationDate } : c));
                                        setSelectedClaim(prev => prev?._id === claim._id ? { ...prev, expirationDate } : prev);
                                        toast.success("Verified FFL expiration date saved. Claim status is unchanged.");
                                    }} />}
                                    {isAdmin && ["Pending", "Rejected", "Cancelled", "Canceled"].includes(claim.status) && <FFLDocumentReview key={`document-${claim._id}`} claim={claim} onDownloadOriginal={openFflDocument} />}
                                    <div className={`button-row${isAdmin ? "" : " dealer-website-actions"}`}>
                                        {isAdmin ? (
                                            <>
                                                {claim.status === "Approved" && (hosting.cancelled ? <span className="verified-website-unavailable" role="status">Website unavailable — hosting cancelled</span> : (
                                                <button
                                                    type="button"
                                                    className="claim-ffl-button view-ffl-button-hollow"
                                                    onClick={() => viewWebsite(claim?.subdomain, claim?.viewToken, claim?.status, claim?._id, claim?.siteStatus, claim?.websiteSubscription?.publiclyAvailable)}
                                                >
                                                    <FontAwesomeIcon icon={["fas", "globe"]} />&nbsp;&nbsp;Website
                                                </button>
                                                ))}
                                                {(claim.status === "Pending" || claim.status === "Cancelled") && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openApproveModal(claim)}
                                                        disabled={!expirationValid}
                                                        title={!expirationValid ? "Verify and save the exact FFL expiration date before approval." : undefined}
                                                        className="claim-ffl-button approve-ffl-button"
                                                    >
                                                        ✅&nbsp;&nbsp;Approve
                                                    </button>
                                                )}
                                                {(claim.status !== "Cancelled") && (
                                                    <button
                                                        type="button"
                                                        className="claim-ffl-button delete-claim-btn"
                                                        onClick={() => openDeleteModal(claim)}
                                                    >
                                                        ❌&nbsp;Cancel Dealership Claim
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {claim.status === "Approved" && (
                                                <button
                                                    type="button"
                                                    className="claim-ffl-button view-ffl-button-hollow"
                                                    onClick={() => viewWebsite(claim?.subdomain, claim?.viewToken, claim?.status, claim?._id, claim?.siteStatus, claim?.websiteSubscription?.publiclyAvailable)}
                                                >
                                                    <FontAwesomeIcon icon="fa-globe" />&nbsp;&nbsp;View Website
                                                </button>
                                                )}
                                                {(claim.status !== "Cancelled") && (
                                                    <button
                                                        type="button"
                                                        className="claim-ffl-button delete-claim-btn"
                                                        onClick={() => openDeleteModal(claim)}
                                                    >
                                                        ❌&nbsp;&nbsp;{["Approved", "Live"].includes(claim.status) ? "Cancel Website Hosting" : "Cancel Dealership Claim"}
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditClaim(claim)}
                                                    className="claim-ffl-button edit-ffl-button"
                                                >
                                                    <FontAwesomeIcon icon="pen-to-square" />&nbsp;&nbsp;Manage Dealership
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {isAdmin && claim.status === "Approved" && <p className="verified-claim-action-note">Cancel Dealership Claim changes claim ownership and directory verification. Hosting-only cancellation is managed in Website Payments → subscription details.</p>}

                                    {!isAdmin && randomTip && (
                                        <div className="note my-ffl-container">
                                            <span className="tip-left">
                                                <span className="tip-icon">💡</span>
                                                <span className="tip-title">Tip:</span>
                                            </span>
                                            <span className="tip-right">{randomTip}</span>
                                        </div>
                                    )}
                                </li>
                            );})}
                        </ul>
                    )
                )}

                {/* Pagination */}
                <Pager
                    safePage={safePage}
                    setPage={setPage}
                    pageCount={pageCount}
                />
            </div>}

            {/* Cancel modal (both) */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Cancel Dealership Claim</h3>
                        {passwordError && <p className="passwordError">{passwordError}</p>}
                        <p><strong><span className="red-exclamation"><FontAwesomeIcon icon="triangle-exclamation" /></span> Your website will go offline.</strong></p>
                        <p>This cancels the dealership claim and removes its current directory verification and website address association. It is not a hosting-subscription cancellation.</p>
                        <p>For a temporary change, use Pause Website. To cancel hosting only, request it through Support Inbox instead.</p>
                        <strong>FFL: {selectedClaim?.fflNumber}</strong><br />
                        <strong>Name: {selectedClaim?.fflName}</strong>

                        <div className="confirmation-input">
                            <label htmlFor="cancel-password" className="cancel-password">
                                Enter {isAdmin ? "admin" : "your"} <strong>password</strong> below to confirm:
                            </label>

                            {/* 👇 Add a real username field so password managers don't
      pick your search input as the username */}
                            <form autoComplete="on">
                                <input
                                    type="text"
                                    name="username"
                                    autoComplete="username"
                                    value={user?.email || ""}
                                    readOnly
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        left: "-9999px",
                                        width: 0,
                                        height: 0,
                                        opacity: 0,
                                        pointerEvents: "none",
                                    }}
                                />

                                <input
                                    id="cancel-password"
                                    name="password"                 // 👈 give it a name
                                    type="password"
                                    autoComplete="current-password" // 👈 correct autocomplete
                                    placeholder={`Enter ${isAdmin ? "admin" : "your"} password to confirm`}
                                    value={password}
                                    ref={passwordRef}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onPaste={(e) => e.preventDefault()}
                                    className="confirmation-input-field"
                                    aria-invalid={password.length > 0 && password.length < 8}
                                />
                            </form>

                            <label htmlFor="cancel-reason" className="mt-8 cancel-reason">
                                Select <strong>cancel reason{isAdmin ? "" : " (optional)"} </strong>:
                            </label>
                            <select
                                id="cancel-reason"
                                className="cancel-reason-dropdown"
                                aria-label="Cancel reason"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                            >
                                {reasonOptions.map(([val, label]) => (
                                    <option key={val || "none"} value={val}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <br />
                        <div className="modal-buttons">
                            <button className="cancel-delete" onClick={closeDeleteModal}>
                                Keep Dealership Claim
                            </button>
                            <button
                                className="confirm-delete"
                                onClick={handleDeleteClaim}
                                disabled={isDeleteDisabled}
                                aria-disabled={isDeleteDisabled}
                            >
                                Cancel Dealership Claim
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Approve modal (admin only) */}
            {isAdmin && showApproveModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Approve FFL Claim</h3>
                        <div id="approve-message" ref={messageRef} className={messageClass} />
                        <p>Are you sure you want to approve this claim?</p>
                        <strong>FFL: {selectedClaim?.fflNumber}</strong><br />
                        <strong>Name: {selectedClaim?.fflName}</strong>

                        <div className="confirmation-input">
                            <label htmlFor="approve-password">Enter admin <strong>password</strong> below to confirm:</label>
                            <form autoComplete="on">
                                <input
                                    type="text"
                                    name="username"
                                    autoComplete="username"
                                    value={user?.email || ""}
                                    readOnly
                                    tabIndex={-1}
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        left: "-9999px",
                                        width: 0,
                                        height: 0,
                                        opacity: 0,
                                        pointerEvents: "none",
                                    }}
                                />
                                <input
                                    id="approve-password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    placeholder="Enter admin password to confirm"
                                    value={password}
                                    ref={passwordRef}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onPaste={(e) => e.preventDefault()}
                                    className="confirmation-input-field"
                                    aria-invalid={password.length > 0 && password.length < 8}
                                />
                            </form>
                        </div>
                        <br />
                        <div className="modal-buttons">
                            <button className="confirm-delete" onClick={closeApproveModal}>No</button>
                            <button
                                className="cancel-delete"
                                onClick={handleApproveClaim}
                                disabled={isApproveDisabled}
                                title={isApproveDisabled ? "Enter password (8+ chars)" : "Approve claim"}
                            >
                                Yes, Approve
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {mfaModalOpen && (
                <EmailVerificationDialog title={`Verify ${mfaAction === 'approve-claim' ? 'Approve' : 'Cancel'} Claim`}>
                        <InlineVerificationCode
                            onSubmit={async (code) => {
                                try {
                                    // Complete the approve action with MFA code
                                    const r = await fetch(
                                        `${Config.API.API_BASE_URL}${Config.ENDPOINTS.ADMIN_CLAIM_COMPLETE}`,
                                        {
                                            method: "POST",
                                            headers: {
                                                "Content-Type": "application/json",
                                                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                                            },
                                            body: JSON.stringify({
                                                claimId: selectedClaim?._id,
                                                challengeId: mfaChallengeId,
                                                code,
                                                mfaAction,
                                                ...(mfaAction === "cancel-claim" ? { cancelReason } : {}),
                                            }),
                                        }
                                    );
                                    const data = await r.json().catch(() => ({}));
                                    if (!r.ok) {
                                        displayMessage(messageRef, data?.message || "Invalid code", "warning", setMessageClass);
                                        toast.error(data?.message || "Invalid code");
                                        return;
                                    }

                                    // Success: remove claim, toast, reset MFA state
                                    setClaims((prev) => prev.filter((c) => c._id !== selectedClaim?._id));
                                    toast.success(`FFL claim ${mfaAction === 'approve-claim' ? 'approved' : 'cancelled'}.`);
                                    clearMfaState();
                                    return;
                                } catch {
                                    toast.error("Verification failed. Please try again.");
                                }
                            }}
                            onResend={async () => {
                                try {
                                    if (mfaAction === "approve-claim") {
                                        const r = await fetch(
                                            `${Config.API.API_BASE_URL}${Config.ENDPOINTS.ADMIN_APPROVE_CLAIM_RESEND}`, // <— add in Config
                                            {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                                                },
                                                body: JSON.stringify({ challengeId: mfaChallengeId }),
                                            }
                                        );
                                        const data = await r.json().catch(() => ({}));
                                        if (!r.ok) {
                                            toast.error(data?.message || "Unable to resend code");
                                        } else {
                                            toast.success("Code resent");
                                            setMfaExpiresAt(data?.expiresAt || null);
                                        }
                                        return;
                                    }
                                } catch {
                                    toast.error("Unable to resend code right now.");
                                }
                            }}
                            messageClass={messageClass}
                            messageRef={messageRef}
                            emailInputRef={{ current: null }}
                            codeExpiresAt={mfaExpiresAt}
                            setExpiresAt={setMfaExpiresAt}
                            setPendingMessage={setPendingMessage}
                            setMessageClass={setMessageClass}
                            setPendingStyle={setPendingStyle}
                            email={formData.email}
                            isLockedOut={false}
                            parent={mfaAction}
                            mfaAction={mfaAction}
                            mfaChallengeId={mfaChallengeId}
                            pendingMessage={pendingMessage}
                            onClose={clearMfaState}
                        />
                </EmailVerificationDialog>
            )}

        </div>
    );
}

export default FFLClaims;

// optional wrappers if you want the same API you have today
export const UserFFLClaims = (props) => <FFLClaims mode="user" {...props} />;
export const AdminFFLClaims = (props) => <FFLClaims mode="admin" {...props} />;
